/**
 * Клиент серверной части.
 *
 * Всё, кроме профиля дела, живёт на сервере: генерация, шаблоны, справочники,
 * подсказки DaData, резервные копии и журнал. Профиль читается и пишется
 * напрямую в поле сделки (`storage/cases.ts`) — так его сохранность
 * не зависит от доступности сервера (ТЗ п. 8.2).
 *
 * Подтверждение подлинности запроса (ТЗ п. 8.3.5): в заголовках уходят домен
 * портала и ключ сеанса, выданный BX24.js. Сервер проверяет их обращением
 * к порталу — подставить чужой идентификатор пользователя нельзя.
 */

import type { BankAccount, CaseProfile, Creditor, Declension, Realty, Vehicle } from '@bd/domain';
import type { DocumentKind } from '@bd/doc-engine';
import { bx24, refreshAuth } from './bx24.js';

/** Организация из DaData, разложенная сервером по полям справочника (FR-13). */
export interface PartySuggestion {
  label: string;
  entry: { name: string; inn: string; ogrn?: string; address: string; source: string };
  warning: string | null;
}

/**
 * Разобранный документ клиента — предложение строк дела. Повторяет ответ
 * сервера (apps/api/src/import/documents.ts); пакет разбора сюда
 * не подключается — он тянет за собой библиотеку чтения PDF.
 */
export interface CreditorsImport {
  kind: 'credit-report-scoring' | 'credit-report-okb' | 'credit-report-nbki' | 'fssp-initiation';
  title: string;
  subject: { fullName: string; birthDate?: string | null; inn: string | null } | null;
  /** Сумма строк сошлась с «Итого» отчёта; null — у документа итога нет */
  totalsMatch: boolean | null;
  rows: Array<{
    creditor: Creditor;
    matched: boolean;
    reportName: string;
    reportKind: string;
    overdueDays: number | null;
    notes?: string[];
  }>;
  /** warning — пропуск требует внимания: долг обнулился после просрочки */
  skipped: Array<{ reportName: string; reason: string; warning?: boolean }>;
}

export interface PropertyImport {
  kind: 'fns-accounts' | 'fns-taxpayer' | 'rosreestr' | 'gibdd';
  title: string;
  subject: { fullName: string; inn: string | null } | null;
  realty: Array<{ realty: Realty; cadastralValue: number | null; notes: string[] }>;
  vehicles: Array<{ vehicle: Vehicle; notes: string[] }>;
  accounts: Array<{ account: BankAccount; bankInn: string | null }>;
  skipped: Array<{ label: string; reason: string; warning?: boolean }>;
}

export interface NdflSummary {
  year: number | null;
  date: string | null;
  agentName: string;
  agentInn: string | null;
  recipientInn: string | null;
  salaryMonths: number[];
  /** Среднемесячная заработная плата (код 2000), коп. */
  averageSalary: number;
  total: number | null;
  /** Компенсация за неиспользованный отпуск (код 2013) — похоже на увольнение */
  looksLikeDismissal: boolean;
}

export interface CurrentJob {
  employer: string;
  registration: string;
  since: string | null;
  position: string;
  partTime: boolean;
}

export interface EgissoMeasure {
  code: string;
  measure: string;
  count: number;
  total: number;
  from: string | null;
  to: string | null;
  periodicity: string;
}

/** Документы блока «Доходы» — у каждого своё предложение. */
export type IncomeImport =
  | { kind: 'ndfl'; title: string; certificates: NdflSummary[]; pagesWithoutText: number[] }
  | { kind: 'work-history'; title: string; currentJobs: CurrentJob[]; lastDismissal: { employer: string; date: string | null } | null }
  | { kind: 'egrip'; title: string; inn: string | null; isEntrepreneur: boolean | null; ogrnip: string | null }
  | { kind: 'egisso'; title: string; measures: Array<EgissoMeasure & { current: boolean }>; description: string; lastPayment: string | null }
  | { kind: 'sfr-pension'; title: string; isRecipient: boolean; details: string }
  | { kind: 'ils'; title: string; asOf: string | null; employers: IlsEmployer[] };

/** Документ для перечня приложений — ответ apps/api/src/import/documents.ts. */
export interface AppendixImport {
  kind: string;
  title: string;
  appendixKey: string | null;
  place: string | null;
  subject: { fullName: string; birthDate?: string | null; inn?: string | null } | null;
  checks: Array<{ ok: boolean; text: string }>;
  /** Отрицательное сальдо ЕНС, коп. */
  taxDebt: { amount: number; confirmingDocument: string } | null;
}

export interface IlsEmployer {
  employer: string;
  lastPeriod: { label: string; from: string | null; to: string | null };
  /** Выплаты за последний период, коп. */
  lastPayments: number;
  /** Среднемесячные выплаты за последний период, коп. */
  monthly: number;
  yearTotal: number;
  year: number | null;
}

/** Разбор любого документа клиента (`/import/any`). */
export interface AnyDocumentImport {
  block: 'creditors' | 'property' | 'income' | 'appendix';
  appendix: AppendixImport;
  creditors?: CreditorsImport;
  property?: PropertyImport;
  income?: IncomeImport;
  blockError?: string;
}

/** Ответ `/api/me`. */
export interface Me {
  userId: string;
  userName: string;
  /** Дошла ли установка до сервера (ключи портала у него есть) */
  installed: boolean;
  /** everyone — приложение открыто всем; admins — администраторам портала и списку «Доступ» */
  accessMode: 'everyone' | 'admins';
  hasAccess: boolean;
  isPortalAdmin: boolean;
  isAdmin: boolean;
}

export interface AccessGrant {
  userId: string;
  userName: string;
  grantedBy: string;
  grantedAt: string;
}

/** Ошибка с перечнем причин — интерфейс показывает их списком (ТЗ п. 8.2). */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly details: string[] = [],
    readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ErrorBody {
  error?: { code?: string; message?: string; details?: string[]; requestId?: string };
}

/** Заголовки подтверждения: домен портала и ключ текущего сеанса. */
function authHeaders(): Record<string, string> {
  const api = bx24();
  const auth = api?.getAuth();
  const headers: Record<string, string> = {};
  if (auth) {
    headers['X-B24-Domain'] = auth.domain;
    headers['X-B24-Auth'] = auth.access_token;
    headers['X-B24-Member'] = auth.member_id;
  }
  return headers;
}

/**
 * Обращение к серверу с обновлением ключа сеанса.
 *
 * Портал выдаёт ключ примерно на час, а дело заполняется столько же
 * или дольше. По истечении сервер отклоняет любой запрос, и карточка
 * переставала работать целиком: пустые справочники, несохранённые копии,
 * отказ при сборке пакета. За две недели такой отказ случился тысячу раз.
 *
 * Повтор здесь безопасен и для записи, в отличие от обрыва связи: отказ
 * по ключу приходит ДО того, как сервер что-либо сделал, — второй папки
 * с пакетом или второй записи в журнале он создать не может.
 */
async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  try {
    return await attempt<T>(path, init);
  } catch (error) {
    if (!(error instanceof ApiError) || error.code !== 'UNAUTHORIZED') throw error;
    if (!(await refreshAuth())) throw error;
    return attempt<T>(path, init);
  }
}

async function attempt<T>(path: string, init: RequestInit = {}): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      ...init,
      headers: { ...authHeaders(), ...(init.headers as Record<string, string> | undefined) },
    });
  } catch {
    // Недоступность сервера не должна выглядеть технической аварией:
    // введённые данные при этом целы, они в карточке дела
    throw new ApiError(
      'Серверная часть недоступна. Введённые данные сохранены в карточке дела, повторите позже',
      'SERVER_UNAVAILABLE',
    );
  }

  if (response.status === 204) return undefined as T;

  const body = (await response.json().catch(() => ({}))) as T & ErrorBody;

  if (!response.ok) {
    const error = body.error ?? {};
    throw new ApiError(
      error.message ?? 'Не удалось выполнить запрос',
      error.code ?? `HTTP_${response.status}`,
      error.details ?? [],
      error.requestId,
    );
  }

  return body as T;
}

/**
 * Чтение с сервера — с повтором при обрыве связи.
 *
 * Связь с сервером у заказчика рвётся коротко и часто: пять обрывов
 * за рабочий день, каждый по несколько секунд. Одна неудачная попытка
 * означала пустой справочник судов и тревожную полосу через всю карточку —
 * при том что через две секунды всё работает. Юрист в этот момент видит
 * «собрать пакет не удастся» и идёт звонить администратору по поводу,
 * которого уже нет.
 *
 * Повторяется ТОЛЬКО чтение. Записи (сохранение, генерация, утверждение)
 * повторять вслепую нельзя: запрос мог дойти до сервера и выполниться,
 * а оборваться уже на ответе — и повтор создал бы вторую папку с пакетом
 * либо вторую запись в журнале.
 *
 * Повтор только при недоступности сервера. Отказ по существу — нет прав,
 * не найдено, неверные данные — повторять бессмысленно: ответ будет тот же.
 */
const RETRY_DELAYS_MS = [400, 1200];

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

async function get<T>(path: string): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await request<T>(path);
    } catch (error) {
      const unavailable = error instanceof ApiError && error.code === 'SERVER_UNAVAILABLE';
      if (!unavailable || attempt >= RETRY_DELAYS_MS.length) throw error;
      await wait(RETRY_DELAYS_MS[attempt] as number);
    }
  }
}

const send = <T>(path: string, body: unknown, method = 'POST') =>
  request<T>(path, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body ?? {}),
  });

/** Загрузка файла: заголовок Content-Type проставляет браузер сам. */
const upload = <T>(path: string, form: FormData) => request<T>(path, { method: 'POST', body: form });

export const api = {
  blankProfile: (caseId: string) => get<{ profile: CaseProfile }>(`/cases/${caseId}/blank`),

  validate: (profile: CaseProfile) =>
    send<{ ok: boolean; errors: string[]; warnings: string[] }>('/cases/validate', { profile }),

  // ---------------------------------------------------------- блокировка

  lock: (caseId: string) =>
    send<{ acquired: boolean; heldBy: string | null; readOnly: boolean }>(`/cases/${caseId}/lock`, {}),

  unlock: (caseId: string) => request<{ released: boolean }>(`/cases/${caseId}/lock`, { method: 'DELETE' }),

  // ------------------------------------------------ резервная копия профиля

  /**
   * Копия профиля на сервере (ТЗ п. 5.1). Хранится зашифрованной, срок
   * хранения задаётся настройкой сервера. Отсюда же пишется журнал.
   */
  backup: (caseId: string, profile: CaseProfile) =>
    send<{ saved: boolean; revision: number }>(`/cases/${caseId}/backup`, { profile }),

  backups: (caseId: string) =>
    get<{ backups: Array<{ revision: number; createdAt: string; expiresAt: string }> }>(`/cases/${caseId}/backups`),

  restoreBackup: (caseId: string, revision: number) =>
    send<{ profile: CaseProfile }>(`/cases/${caseId}/backups/${revision}/restore`, {}),

  journal: (caseId: string) =>
    get<{ entries: Array<{ createdAt: string; userId: string | null; action: string; comment: string | null }> }>(
      `/cases/${caseId}/journal`,
    ),

  // ---------------------------------------------------------- генерация

  generate: (body: {
    caseId: string;
    profile: CaseProfile;
    documents?: DocumentKind[];
    formats?: Array<'docx' | 'pdf'>;
    folderId?: string;
    templateVersions?: Partial<Record<DocumentKind, number>>;
  }) =>
    send<{
      packageVersion: number;
      folderId: string;
      files: Array<{ kind: DocumentKind; fileName: string; format: 'docx' | 'pdf'; fileId?: string }>;
      warnings: string[];
      durationMs: number;
      /** Кредиторы, впервые внесённые в справочник этой сборкой */
      creditorsAdded?: string[];
    }>('/generate', body),

  /**
   * Скачивание готового документа.
   *
   * Файл забирается запросом с ключом сеанса, а не по ссылке портала:
   * та содержит ключ доступа приложения ко всему REST — переслав её,
   * юрист отдал бы вместе с документом доступ ко всей CRM.
   */
  downloadFile: async (caseId: string, fileId: string, fileName: string): Promise<void> => {
    const response = await fetch(`/api/cases/${caseId}/files/${fileId}`, { headers: authHeaders() });
    if (!response.ok) throw new ApiError('Не удалось скачать файл', `HTTP_${response.status}`);

    // Файл отдаётся браузеру ссылкой на данные в памяти: во вкладке портала
    // обычная ссылка на скачивание работает не всегда
    const url = URL.createObjectURL(await response.blob());
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      URL.revokeObjectURL(url);
    }
  },

  versions: (caseId: string, folderId?: string) =>
    get<{ folderId: string; versions: Array<{ version: number; files: Array<{ id: string; name: string; createTime?: string }> }> }>(
      `/cases/${caseId}/versions${folderId ? `?folderId=${encodeURIComponent(folderId)}` : ''}`,
    ),

  preview: (profile: CaseProfile, documentKind: DocumentKind = 'zayavlenie', previous?: string[]) =>
    send<{ paragraphs: string[]; diff: Array<{ status: string; text: string }> | null; templateVersion: number }>(
      '/preview',
      { profile, documentKind, previous },
    ),

  // ---------------------------------------------------------- склонения

  decline: (
    name: { last: string; first: string; middle?: string; gender: 'male' | 'female' | null },
    current?: Declension,
  ) => send<{ declension: Declension }>('/linguistics/decline', { name, current }),

  // -------------------------------------------------------- справочники

  dictionary: (name: string, params: { search?: string; limit?: number } = {}) => {
    const query = new URLSearchParams();
    if (params.search) query.set('search', params.search);
    if (params.limit) query.set('limit', String(params.limit));
    const suffix = query.toString();
    return get<{ entries: Array<Record<string, unknown> & { id: string; name: string }> }>(
      `/dictionaries/${name}${suffix ? `?${suffix}` : ''}`,
    );
  },

  saveDictionaryEntry: (name: string, entry: Record<string, unknown>) =>
    send<{ entry: Record<string, unknown> }>(`/dictionaries/${name}`, entry),

  archiveDictionaryEntry: (name: string, id: string, archived: boolean) =>
    send<{ entry: Record<string, unknown> }>(`/dictionaries/${name}/${id}/archive`, { archived }),

  /** Предзаполнение суда по субъекту РФ (правило R-01). */
  courtByRegion: (regionCode: string) =>
    get<{ court: (Record<string, unknown> & { name: string; address: string }) | null }>(
      `/dictionaries/courts/by-region/${regionCode}`,
    ),

  // ------------------------------------------------------------ шаблоны

  templateVersions: (kind: DocumentKind) =>
    get<{
      versions: Array<{
        id: string;
        version: number;
        uploadedAt: string;
        uploadedBy: string | null;
        isCurrent: boolean;
        /** Убрана из рабочего списка, но связь с собранными документами цела */
        archived: boolean;
        comment: string | null;
      }>;
    }>(`/templates/${kind}/versions`),

  /**
   * Разбор документа клиента в строки дела. Файл на сервере не сохраняется.
   * Уже внесённое (УИД договоров, номера счетов) идёт ДО файла: сервер
   * читает поля формы, предшествующие ему.
   */
  importCreditorsDocument: (caseId: string, file: File, existingUids: string[]) => {
    const form = new FormData();
    form.append('existing', JSON.stringify(existingUids));
    form.append('file', file, file.name);
    return upload<CreditorsImport>(`/cases/${caseId}/import/creditors`, form);
  },

  /**
   * Выписки об имуществе: ЕГРН (ФНС и Росреестр), ГИБДД, счета (форма 9ф).
   * `existing` — «realty:<к/н>», «vehicle:<госномер или VIN>», «account:<номер>»;
   * `debtor` — ФИО должника, по нему в выписке Росреестра находится его право.
   */
  importPropertyDocument: (caseId: string, file: File, existing: string[], debtor: string) => {
    const form = new FormData();
    form.append('existing', JSON.stringify(existing));
    form.append('debtor', debtor);
    form.append('file', file, file.name);
    return upload<PropertyImport>(`/cases/${caseId}/import/property`, form);
  },

  importAppendixDocument: (caseId: string, file: File) => {
    const form = new FormData();
    form.append('file', file, file.name);
    return upload<AppendixImport>(`/cases/${caseId}/import/appendix`, form);
  },

  importIncomeDocument: (caseId: string, file: File) => {
    const form = new FormData();
    form.append('file', file, file.name);
    return upload<IncomeImport>(`/cases/${caseId}/import/income`, form);
  },

  /**
   * Любой документ клиента: сервер сам определяет вид и разбирает его
   * для своего раздела и для перечня приложений (загрузка папки в «Должнике»).
   * `existing` — УИД договоров и ключи имущества вперемешку: каждый разбор
   * узнаёт свои.
   */
  importAnyDocument: (caseId: string, file: File, existing: string[], debtor: string) => {
    const form = new FormData();
    form.append('existing', JSON.stringify(existing));
    form.append('debtor', debtor);
    form.append('file', file, file.name);
    return upload<AnyDocumentImport>(`/cases/${caseId}/import/any`, form);
  },

  uploadTemplate: (kind: DocumentKind, file: File, comment: string) => {
    const form = new FormData();
    form.append('comment', comment);
    form.append('file', file, file.name);
    return upload<{ version: number; warnings: string[] }>(`/templates/${kind}`, form);
  },

  /** Проверка разметки без загрузки (FR-42). */
  checkTemplateFile: (file: File) => {
    const form = new FormData();
    form.append('file', file, file.name);
    return upload<{
      ok: boolean;
      issues: Array<{ severity: 'error' | 'warning'; message: string }>;
      variables: string[];
      sections: string[];
    }>('/templates/check', form);
  },

  /**
   * Убрать редакцию шаблона в архив либо вернуть её в список.
   *
   * Удаления редакций нет: журнал сборки ссылается на них, и по этой ссылке
   * восстанавливают, каким шаблоном собран документ по настоящему делу.
   */
  archiveTemplate: (kind: DocumentKind, version: number, archived: boolean) =>
    send<{ version: number; archived: boolean }>(`/templates/${kind}/${version}/archive`, { archived }),

  setCurrentTemplate: (kind: DocumentKind, version: number) =>
    send<{ version: number }>(`/templates/${kind}/${version}/current`, {}),

  // ------------------------------------------------------------- доступ

  /** Кто открыл приложение и открыто ли оно ему (закрытая установка «Чистый Лист»). */
  me: () => get<Me>('/me'),

  /**
   * Завершение установки из приложения, если событие установки до сервера
   * не дошло. Передаётся ключ сеанса администратора — так же, как его
   * присылает сам портал при установке; сервер проверяет, что это администратор.
   */
  finishSetup: () => {
    const auth = bx24()?.getAuth();
    if (!auth || !auth.refresh_token) return Promise.reject(new ApiError('Портал не выдал ключ для установки', 'NO_AUTH'));
    return send<{ installed: boolean; already: boolean; fields?: { created: string[]; failed: Array<{ title: string; reason: string }> } | null; bind?: { ok: boolean; reason?: string } | null }>(
      '/setup',
      { refreshToken: auth.refresh_token, expiresIn: auth.expires_in, memberId: auth.member_id },
    );
  },

  access: {
    list: () => get<{ grants: AccessGrant[] }>('/access'),
    grant: (users: Array<{ id: string; name: string }>) => send<{ grants: AccessGrant[] }>('/access', { users }),
    revoke: (userId: string) => request<{ grants: AccessGrant[] }>(`/access/${userId}`, { method: 'DELETE' }),
  },

  // ------------------------------------------------------------- DaData

  dadata: {
    status: () =>
      get<{ enabled: boolean; addressSuggestions: boolean; cleanerAvailable: boolean }>('/dadata/status'),

    party: (query: string) =>
      send<{ enabled: boolean; suggestions: PartySuggestion[] }>('/dadata/party/suggest', { query }),

    findByInn: (inn: string) => send<{ suggestions: PartySuggestion[] }>('/dadata/party/find', { inn }),

    /**
     * Ввоз организации в справочник кредиторов (FR-13). Источником данных для
     * документов остаётся справочник, а не DaData: он переживёт отключение
     * внешнего сервиса.
     */
    importParty: (inn: string) =>
      send<{ entry: Record<string, unknown> & { id: string; name: string }; created: boolean; warning: string | null }>(
        '/dadata/party/import',
        { inn },
      ),

    bank: (query: string) =>
      send<{ enabled: boolean; suggestions: Array<{ label: string; bankName: string; bic: string }> }>(
        '/dadata/bank/suggest',
        { query },
      ),
  },
};
